import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React, { CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';
import { useEditPath } from '@/components/editor-preview/EditorPreviewContext';

import {
    DomainMovieConfiguration,
    PageLocale,
    TopBannerConfiguration
} from '@cineamo/api/models/premium-movie-landing-page-configuration/PremiumMovieLandingPageConfigurationDto';

import { ID_ALS_WAERE_ES_LEICHT } from '@cineamo/api/constants/movie-ids';
import { TMDB_IMAGE_RESOLUTION } from '@cineamo/api/constants/tmdb-constants';

import { getMovieBackdropImages } from '@cineamo/api/helpers/movie-helper';

import { copyToClipboard } from '@cineamo/ui/src/helpers/clipboard-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';

import Button from '@cineamo/ui/src/button/Button';
import { IconMenu24 } from '@cineamo/ui/src/icons/ic-menu';
import { IconPlay16 } from '@cineamo/ui/src/icons/ic-play';
import { IconPlayVideo16 } from '@cineamo/ui/src/icons/ic-playVideo';
import { IconShareBox24 } from '@cineamo/ui/src/icons/ic-share-box';
import { IconTicket16 } from '@cineamo/ui/src/icons/ic-ticket';

import CenterLayout from '@cineamo/composables/src/CenterLayout';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { buildOffsetCss, MOBILE_MAX_WIDTH_PX } from '@/utils/landing-page/page-layout';

import { HOME_ENTERTAINMENT_ANCHOR_ID, useHomeEntertainment } from './home-entertainment/useHomeEntertainment';
import PageMenu from './PageMenu';
import ShowtimesFinderMenu, { SHOWTIMES_FINDER_ANCHOR } from './showtimes-finder/ShowtimesFinderMenu';
import { useTrailer } from './useTrailer';

/**
 * Whether `targetUrl` leaves this landing page, and so deserves its own tab.
 *
 * Resolved against the configured domain rather than `window.location`, so the answer
 * is identical on the server and the client — comparing against the live location
 * would render differently in the two passes and trip hydration.
 *
 * Anything that is not an absolute `http(s)` URL — a bare `#contest`, a `/de/…` path —
 * is by definition on this page. An absolute URL still counts as internal when its
 * host is this landing page's own domain, which is the common shape an admin pastes
 * for an in-page jump: the full address copied from the browser, anchor included.
 */
function leavesThisPage(targetUrl: string, domain: DomainMovieConfiguration['domain']): boolean {
    if (!/^https?:\/\//i.test(targetUrl)) {
        return false;
    }
    if (!domain) {
        return true;
    }
    const stripWww = (host: string): string => host.replace(/^www\./i, '').toLowerCase();
    try {
        return stripWww(new URL(targetUrl).host) !== stripWww(domain);
    } catch {
        return true;
    }
}

/** Whether the URL currently points at the showtimes finder. */
const isFinderHash = (): boolean => window.location.hash.slice(1) === SHOWTIMES_FINDER_ANCHOR;

const Banner: React.FC<TopBannerConfiguration> = ({
    backgroundColor = '#FFFFFF',
    textColor = '#000000',
    text,
    targetUrl
}) => {
    const locale = useLocale() as PageLocale;
    const { domainConfiguration } = useDomainConfiguration();
    const editPath = useEditPath('topBanner');

    const style: CSSProperties = {
        backgroundColor: backgroundColor,
        color: textColor
    };

    // A bar without text in this language would be an empty stripe.
    if (!text?.[locale]) {
        return null;
    }

    const content = (
        <CenterLayout className="flex min-h-48 px-9 py-9 lg:py-12 items-center justify-center">
            {text?.[locale] && (
                <span
                    className="text-center whitespace-pre-wrap text-body-bold-12 lg:text-body-bold-14"
                    dangerouslySetInnerHTML={{
                        __html: text[locale]
                    }}
                />
            )}
        </CenterLayout>
    );

    if (!targetUrl) {
        return (
            <div
                className="w-full z-[1]"
                style={style}
                {...editPath}>
                {content}
            </div>
        );
    }

    const isExternal = leavesThisPage(targetUrl, domainConfiguration.domain);

    return (
        <a
            className="block w-full z-[1]"
            style={style}
            {...editPath}
            href={targetUrl}
            {...(isExternal ? { target: '_blank', rel: 'noreferrer' } : {})}>
            {content}
        </a>
    );
};

const cssPropertiesObjectToString = (style: CSSProperties): string =>
    Object.entries(style)
        .map(([property, value]) => `${property.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${String(value)}`)
        .join('; ');

const generateResponsiveCSS = (selector: string, desktopCss: string, mobileCss: string): string => {
    let css = '';
    if (desktopCss) {
        css += `${selector} { ${desktopCss} } `;
    }
    if (mobileCss) {
        css += `@media (max-width: ${MOBILE_MAX_WIDTH_PX}px) { ${selector} { ${mobileCss} } }`;
    }
    return css;
};

const PageHeroContent: React.FC<{
    onWatchTrailerClick: () => void;
    onPrimaryCtaClick: () => void;
    onShareClick: () => void;
    onMenuClick: () => void;
}> = ({ onWatchTrailerClick, onPrimaryCtaClick, onShareClick, onMenuClick }) => {
    const t = useTranslations();
    const { domainConfiguration, movie, movieDto } = useDomainConfiguration();
    const { isActive: isHomeEntertainment, buttonLabel: homeEntertainmentLabel } = useHomeEntertainment();
    const trailer = useTrailer();
    const artworkEditPath = useEditPath('heroArtwork');
    const buttonsEditPath = useEditPath('heroButtons');
    // Without an uploaded backdrop the hero shows the film's best text-free backdrop instead of a black box.
    const heroImage = useMemo(
        () =>
            domainConfiguration.heroBackdropUrl ||
            getMovieBackdropImages(movieDto, {
                imageOptions: { resolution: TMDB_IMAGE_RESOLUTION.W1280, includeImagesWithoutLocale: true }
            })[0],
        [domainConfiguration.heroBackdropUrl, movieDto]
    );
    const movieTitle = movie.title;

    const heroStyleTag = useMemo(() => {
        const legacyCss = domainConfiguration.heroContentStyle
            ? cssPropertiesObjectToString(domainConfiguration.heroContentStyle)
            : '';

        const contentDesktop = [legacyCss, domainConfiguration.heroContentStyleList ?? ''].filter(Boolean).join('; ');

        const css =
            generateResponsiveCSS(
                '[data-hero-content]',
                contentDesktop,
                domainConfiguration.heroContentStyleListMobile ?? ''
            ) +
            generateResponsiveCSS(
                '[data-hero-buttons]',
                domainConfiguration.heroButtonsStyleList ?? '',
                domainConfiguration.heroButtonsStyleListMobile ?? ''
            ) +
            // Last, so the editor's offsets sit on top of any legacy style above.
            buildOffsetCss(domainConfiguration.elementOffsets);

        return css;
    }, [
        domainConfiguration.heroContentStyle,
        domainConfiguration.heroContentStyleList,
        domainConfiguration.heroContentStyleListMobile,
        domainConfiguration.heroButtonsStyleList,
        domainConfiguration.heroButtonsStyleListMobile,
        domainConfiguration.elementOffsets
    ]);

    return (
        <div className="relative w-full h-full flex flex-col">
            {heroStyleTag && <style dangerouslySetInnerHTML={{ __html: heroStyleTag }} />}
            {/* Distributor or Custom Text Banner */}
            {domainConfiguration.topBanner && <Banner {...domainConfiguration.topBanner} />}
            <div className="relative w-full h-full">
                <div className="absolute left-0 top-0 w-full h-full flex flex-col">
                    {/* {domainConfiguration.premiumMovieVideoUrl && !heroImage && (
                            <ReactPlayer
                                className=" -z-10 w-full h-full pointer-events-none"
                                width="100%"
                                height="100%"
                                playing={true}
                                config={{
                                    youtube: {
                                        playerVars: {
                                            showInfo: 0,
                                            modestbranding: 0,
                                            rel: 0
                                        }
                                    }
                                }}
                                playIcon={null}
                                controls={false}
                                muted
                                playsinline
                                loop
                                url={domainConfiguration.premiumMovieVideoUrl}
                            />
                        )} */}

                    {heroImage && (
                        <Image
                            className="w-full h-full object-cover"
                            src={heroImage}
                            width={10000}
                            height={10000}
                            alt=""
                        />
                    )}
                </div>
                {![ID_ALS_WAERE_ES_LEICHT].includes(domainConfiguration.movieId) && (
                    <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-b from-black/20 to-black/0" />
                )}

                <div className="relative w-full h-full flex flex-col items-center">
                    <div
                        className="px-16 py-9 w-full flex flex-row justify-between"
                        style={
                            ID_ALS_WAERE_ES_LEICHT === domainConfiguration.movieId
                                ? {
                                      color: '#565656'
                                  }
                                : {}
                        }>
                        <button
                            className="p-12 cursor-pointer"
                            onClick={onMenuClick}
                            aria-label={t('common.menu')}>
                            <IconMenu24 aria-hidden="true" />
                        </button>
                        <button
                            className="p-12 cursor-pointer"
                            onClick={() => onShareClick()}
                            aria-label={t('common.share')}>
                            <IconShareBox24 aria-hidden="true" />
                        </button>
                    </div>
                    <div
                        className="my-auto flex flex-col space-y-48 items-center"
                        data-hero-content>
                        {domainConfiguration.titleArtworkUrl && (
                            <div
                                data-hero-artwork
                                {...artworkEditPath}
                                className={cn(
                                    'w-[80vw] max-h-[220px] h-auto',
                                    domainConfiguration.titleArtworkOrientation === 'portrait'
                                        ? 'max-w-[416px]'
                                        : 'max-w-[740px]'
                                )}>
                                <Image
                                    className="w-full h-full object-contain"
                                    src={domainConfiguration.titleArtworkUrl}
                                    width={10000}
                                    height={10000}
                                    alt={`${movieTitle}`}
                                />
                            </div>
                        )}
                        <div
                            className="flex flex-col lg:flex-row gap-24 px-72 lg:px-0 w-full lg:w-auto"
                            data-hero-buttons
                            {...buttonsEditPath}>
                            {trailer && (
                                <Button
                                    className="!rounded-8 w-full lg:w-auto h-48"
                                    style={{
                                        backgroundColor: domainConfiguration.style.primaryColor,
                                        color: 'var(--on-primary-color)'
                                    }}
                                    onClick={onWatchTrailerClick}
                                    leadingIcon={<IconPlay16 className="w-24 h-24 flex-none" />}>
                                    {t('common.watch-trailer')}
                                </Button>
                            )}
                            <Button
                                className="!rounded-8 w-full lg:w-auto h-48"
                                style={{
                                    backgroundColor: domainConfiguration.style.primaryColor,
                                    color: 'var(--on-primary-color)'
                                }}
                                onClick={onPrimaryCtaClick}
                                leadingIcon={
                                    isHomeEntertainment ? (
                                        <IconPlayVideo16 className="w-24 h-24 flex-none" />
                                    ) : (
                                        <IconTicket16 className="w-24 h-24 flex-none" />
                                    )
                                }>
                                {isHomeEntertainment ? homeEntertainmentLabel : t('common.find-tickets')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PageHeader: React.FC<{
    onWatchTrailerClick: () => void;
    onPlotClick: () => void;
    onInfoClick: () => void;
}> = ({ onWatchTrailerClick, onPlotClick, onInfoClick }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isShowtimesAndTicketsOpen, setIsShowtimesAndTicketsOpen] = useState(false);
    // True only for the frame in which a `#showings` deep link opens the finder, so that open is
    // painted rather than animated: the finder is what the link asked for, not a transition away
    // from the hero. Cleared right after, so closing it again slides as usual.
    const [skipFinderOpenAnimation, setSkipFinderOpenAnimation] = useState(false);
    // Whether the entry currently in the history belongs to the finder, so closing it can pop that
    // entry instead of stacking another. False for a visitor who arrived on `#showings`: nothing of
    // ours sits behind them, and Back has to keep meaning "leave the page".
    const pushedFinderHistoryEntry = useRef(false);
    const t = useTranslations();
    const { track } = useAnalytics();
    const { isActive: isHomeEntertainment } = useHomeEntertainment();
    const headerRef = useRef<HTMLDivElement>(null);
    const heroEditPath = useEditPath('hero');

    useEffect(() => {
        // A hash in the URL is a deliberate deep link (e.g. /gewinnspiel redirects to /#contest).
        // The browser has already jumped to that section, so pulling the hero back into view here
        // would yank the viewport to the top before ScrollToAnchor scrolls back down again.
        if (window.location.hash) return;
        headerRef.current?.scrollIntoView(false);
    }, []);

    const sharePageLink = useCallback(async () => {
        const link = window.location.href;
        const shareData = {
            url: link
        };
        if (!navigator.canShare?.(shareData)) {
            copyToClipboard(link, t('showing-info.copied'));
            return;
        }
        track('Page Shared');
        await navigator.share(shareData).catch(() => {});
    }, [t, track]);

    const openShowtimesAndTickets = useCallback(() => {
        setIsShowtimesAndTicketsOpen(true);
        track('Page Showtimes and Tickets Opened');
        // Put the finder in the URL, so the address is a link into the showings — which is also what
        // the share button copies — and so Back has something of ours to pop. Next supports the
        // native history methods for exactly this, and `pushState` fires no `hashchange`, so the
        // listener below never sees our own writes.
        if (!isFinderHash()) {
            window.history.pushState(null, '', `#${SHOWTIMES_FINDER_ANCHOR}`);
            pushedFinderHistoryEntry.current = true;
        }
    }, [track]);

    const closeShowtimesAndTickets = useCallback(() => {
        setIsShowtimesAndTicketsOpen(false);
        if (pushedFinderHistoryEntry.current) {
            // Pop the finder's own entry rather than adding another, so closing and Back are the
            // same step and the history does not collect one entry per open.
            pushedFinderHistoryEntry.current = false;
            window.history.back();
            return;
        }
        // Arrived on the deep link, so there is no entry of ours behind this one: drop the hash
        // instead. Leaving it in place would also stop the same `#showings` link from opening the
        // finder a second time, since an unchanged hash fires no `hashchange`.
        if (isFinderHash()) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
    }, []);

    // The hash is the finder's state, in both directions: `#showings` opens it — the deep link into
    // the showings, as in https://www.adamsacht.de/de#showings — and leaving the hash, whether by
    // Back or by a link elsewhere on the page, closes it again.
    //
    // A layout effect rather than an effect: it runs between hydration and the first paint, so a
    // visitor arriving on the deep link has the finder on screen with the page rather than a frame
    // later. `popstate` covers Back and Forward, `hashchange` a link on the page itself — a
    // configured top banner pointing at the hash, say — which changes nothing but the hash.
    useLayoutEffect(() => {
        // In home-entertainment mode no finder is rendered, so the hash has nothing to open.
        if (isHomeEntertainment) {
            return;
        }

        const syncWithHash = () => {
            if (isFinderHash()) {
                openShowtimesAndTickets();
            } else {
                setIsShowtimesAndTicketsOpen(false);
            }
            // Whichever navigation landed here, the entry is the browser's own — ours to pop when
            // the finder closes, and gone again the moment the hash is something else.
            pushedFinderHistoryEntry.current = isFinderHash();
        };

        let restoreAnimationFrame: number | undefined;
        if (isFinderHash()) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: the hash exists only on the client, so the open cannot come from render
            setSkipFinderOpenAnimation(true);
            openShowtimesAndTickets();
            // Two frames, not one: a single `requestAnimationFrame` still runs before the open is
            // painted, and restoring the transition that early makes it slide in after all.
            restoreAnimationFrame = requestAnimationFrame(() => {
                restoreAnimationFrame = requestAnimationFrame(() => setSkipFinderOpenAnimation(false));
            });
        }

        window.addEventListener('popstate', syncWithHash);
        window.addEventListener('hashchange', syncWithHash);
        return () => {
            window.removeEventListener('popstate', syncWithHash);
            window.removeEventListener('hashchange', syncWithHash);
            if (restoreAnimationFrame !== undefined) {
                cancelAnimationFrame(restoreAnimationFrame);
            }
        };
    }, [isHomeEntertainment, openShowtimesAndTickets]);

    // In home-entertainment mode the hero's ticket CTA becomes the offers CTA: instead of opening
    // the showtimes finder it jumps to the offers section further down the page.
    const onPrimaryCtaClick = useCallback(() => {
        if (!isHomeEntertainment) {
            openShowtimesAndTickets();
            return;
        }
        track('Page Home Entertainment Clicked');
        document.getElementById(HOME_ENTERTAINMENT_ANCHOR_ID)?.scrollIntoView({
            block: 'start',
            behavior: 'smooth'
        });
    }, [isHomeEntertainment, openShowtimesAndTickets, track]);

    const openMenu = useCallback(() => {
        setIsMenuOpen(true);
        track('Page Menu Opened');
    }, [track]);

    return (
        <div
            className="relative w-full min-h-[100dvh] h-[100dvh] lg:h-[100dvh] flex flex-col"
            ref={headerRef}
            {...heroEditPath}>
            <PageMenu
                isMenuOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                onPrimaryCtaClick={onPrimaryCtaClick}
                onWatchTrailerClick={onWatchTrailerClick}
                onPlotClick={onPlotClick}
                onInfoClick={onInfoClick}
                onShareClick={sharePageLink}
            />
            {!isHomeEntertainment && (
                <ShowtimesFinderMenu
                    isMenuOpen={isShowtimesAndTicketsOpen}
                    skipOpenAnimation={skipFinderOpenAnimation}
                    onMenuClick={openMenu}
                    onClose={closeShowtimesAndTickets}
                    onShareClick={sharePageLink}
                />
            )}
            <PageHeroContent
                onWatchTrailerClick={onWatchTrailerClick}
                onPrimaryCtaClick={onPrimaryCtaClick}
                onShareClick={sharePageLink}
                onMenuClick={openMenu}
            />
        </div>
    );
};

export default PageHeader;
